import { useState, useRef, useEffect, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import ClassString from '@utils/class-string.ts';
import { createPortal } from 'react-dom';
import useTooltipContainer from '@hooks/use-tooltip-container.tsx';
import scrollStore from '@store/scroll-store.ts';
import isMobile from '@utils/is-mobile.ts';
import { autorun } from 'mobx';

interface TooltipProps {
	text: string | ReactNode;
	children: ReactNode;
	direction?: 'right' | 'bottom' | 'left' | 'top';
	className?: string;
	widthClass?: string;
	fullWidth?: boolean;
}

const defaultWidth = 250;
const arrowWidth = 8;
const tooltipEdgeMargin = 5;

const getXY = (
	direction: 'right' | 'bottom' | 'left' | 'top',
	tooltipRect: DOMRect | undefined,
	wrapperRect: DOMRect | undefined
) => {
	if (!wrapperRect) {
		return { x: 0, y: 0 };
	}

	const centerX = wrapperRect.left + wrapperRect.width / 2;
	const centerY = wrapperRect.top + wrapperRect.height / 2;

	let adjustmentX: number | undefined;
	if (tooltipRect && tooltipRect.left < 0) {
		adjustmentX = Math.abs(tooltipRect.left) + tooltipEdgeMargin;
	}

	if (tooltipRect && tooltipRect.right > window.innerWidth) {
		adjustmentX =
			-Math.abs(window.innerWidth - tooltipRect.right) -
			tooltipEdgeMargin;
	}

	switch (direction) {
		case 'right':
			return { x: wrapperRect.right + arrowWidth, y: centerY };
		case 'bottom':
			return {
				x: centerX,
				y: wrapperRect.bottom + arrowWidth,
				adjustmentX,
			};
		case 'left':
			return { x: wrapperRect.left - arrowWidth, y: centerY };
		case 'top':
			return { x: centerX, y: wrapperRect.top - arrowWidth, adjustmentX };
	}
};

const Tooltip = observer(function Tooltip(props: TooltipProps) {
	const [visible, setVisible] = useState(false);
	const [beenVisible, setBeenVisible] = useState(false);
	const [adjustedDirection, setAdjustedDirection] = useState(
		props.direction ?? 'right'
	);
	const [width, setWidth] = useState<number>(defaultWidth);
	const tooltipWrapperRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);

	const [isTooltipReady, setIsTooltipReady] = useState(false);
	useEffect(() => {
		if (
			tooltipRef.current &&
			tooltipWrapperRef.current &&
			!isTooltipReady
		) {
			setIsTooltipReady(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tooltipRef.current, tooltipWrapperRef.current]);

	const container = useTooltipContainer();

	useEffect(() => {
		if (visible && tooltipRef.current) {
			const tooltipRect = tooltipRef.current.getBoundingClientRect();
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			let newWidth: number = defaultWidth;

			if (adjustedDirection === 'right') {
				const difference = tooltipRect.right - viewportWidth + 20;
				newWidth = Math.max(
					Math.min(tooltipRect.width - difference, defaultWidth),
					0
				);

				if (newWidth < 50) {
					newWidth = defaultWidth;
					setAdjustedDirection('left');
				}

				setWidth(newWidth);
				return;
			}

			if (adjustedDirection === 'left') {
				const difference = tooltipRect.left - 10;
				newWidth = Math.max(
					Math.min(tooltipRect.width + difference, defaultWidth),
					0
				);
				if (newWidth < 50) {
					newWidth = defaultWidth;
					setAdjustedDirection('right');
				}

				setWidth(newWidth);
				return;
			}

			if (adjustedDirection === 'bottom') {
				if (tooltipRect.bottom < 0) {
					setAdjustedDirection('top');
					return;
				}
			}

			if (adjustedDirection === 'top') {
				if (tooltipRect.top > viewportHeight) {
					setAdjustedDirection('bottom');
					return;
				}
			}
		}
	}, [visible, props.direction, adjustedDirection]);

	// Timeout ref using correct browser-safe typing
	const touchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (touchTimeoutRef.current !== null) {
				clearTimeout(touchTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const disposer = autorun(() => {
			scrollStore.scrollPosition;

			if (touchTimeoutRef.current !== null) {
				clearTimeout(touchTimeoutRef.current);
				setVisible(false);
			}
		});

		return () => disposer();
	}, []);

	const handleTouch = () => {
		if (!beenVisible) {
			setBeenVisible(true);
		}
		setVisible(true);

		// Clear any existing timeout to avoid overlapping
		if (touchTimeoutRef.current !== null) {
			clearTimeout(touchTimeoutRef.current);
		}

		// Set a new timeout to hide after 3s
		touchTimeoutRef.current = setTimeout(() => {
			setVisible(false);
			touchTimeoutRef.current = null;
		}, 3000);
	};

	if (!container) {
		return <>{props.children}</>;
	}

	const tooltipRect = tooltipRef.current?.getBoundingClientRect();
	const wrapperRect = tooltipWrapperRef.current?.getBoundingClientRect();
	const position = getXY(adjustedDirection, tooltipRect, wrapperRect);
	// Update component when scroll position updates.
	scrollStore.scrollPosition;

	return (
		<div
			className={ClassString({
				static: 'relative flex items-center justify-center',
				dynamic: {},
				custom: props.className,
			})}
		>
			<div
				onMouseEnter={() => {
					if (isMobile) {
						return;
					}

					if (!beenVisible) {
						setBeenVisible(true);
					}
					setVisible(true);
				}}
				onMouseLeave={() => {
					if (isMobile) {
						return;
					}

					setVisible(false);
				}}
				onTouchStart={handleTouch}
				ref={tooltipWrapperRef}
			>
				{props.children}
			</div>
			{createPortal(
				<div
					ref={tooltipRef}
					className={ClassString({
						static: 'absolute translate w-fit text-sm z-10 pointer-events-none opacity-0',
						dynamic: {
							'w-screen': props.fullWidth,
							'-translate-y-1/2': adjustedDirection === 'right',
							'-translate-x-1/2': adjustedDirection === 'bottom',
							'-translate-x-full -translate-y-1/2':
								adjustedDirection === 'left',
							'-translate-x-1/2 -translate-y-full':
								adjustedDirection === 'top',
							'animate-tooltip-show': visible,
							'animate-tooltip-hide': beenVisible && !visible,
						},
						custom: props.widthClass,
					})}
					style={{
						maxWidth: width,
						left: position.x,
						top: position.y,
					}}
				>
					<div
						className={ClassString({
							static: 'absolute transform w-0 h-0 border-gray-500',
							dynamic: {
								'-left-2 top-1/2 -translate-y-1/2 border-t-[7px] border-t-transparent border-r-[8px]  border-b-[7px] border-b-transparent':
									adjustedDirection === 'right',
								'-top-2 left-1/2 -translate-x-1/2 border-l-[7px] border-l-transparent border-b-[8px] border-r-[7px] border-r-transparent':
									adjustedDirection === 'bottom',
								'-right-2 top-1/2 -translate-y-1/2 border-t-[7px] border-t-transparent border-l-[8px] border-b-[7px] border-b-transparent':
									adjustedDirection === 'left',
								'-bottom-2 left-1/2 -translate-x-1/2 border-l-[7px] border-l-transparent border-t-[8px] border-r-[7px] border-r-transparent':
									adjustedDirection === 'top',
							},
						})}
					></div>
					<div
						className={
							'p-2 bg-gray-500 rounded-xl text-white font-semibold w-fit transform '
						}
						style={{
							transform: position.adjustmentX
								? `translateX(${position.adjustmentX}px)`
								: undefined,
						}}
					>
						{props.text}
					</div>
				</div>,
				container
			)}
		</div>
	);
});

export default Tooltip;
